import React, { useState, lazy, Suspense, useEffect } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { Collapse, Box, Tabs, Tab, Typography, MenuItem, Menu, Chip } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme, alpha } from "@mui/material/styles";
import { useMediaQuery } from '@mui/material';
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-material.css";
import LockIcon from '@mui/icons-material/Lock';
// Tab icons
import AssessmentIcon from "@mui/icons-material/Assessment";
import CodeIcon from "@mui/icons-material/Code";
import LayersIcon from "@mui/icons-material/Layers";

// Feature flags
import { getDevFlag } from "../../constants/devFlags";

import {
  setSelectedDashboardId,
  setGlobalActiveTickers,
} from "../../utils";
import { SectionHeaderProvider } from "../../components/common/SectionHeader";
import { ControlButton } from "../../components/common/controls";
import { useWorkspaceFiltersOpen } from "../../components/common/AnalyticsWorkspace";
import { useSubscriptionStatus } from "../../hooks";

const UnifiedMetrics     = lazy(() => import("./subpages/UnifiedMetrics"));
const MetricsDeveloper   = lazy(() => import("./subpages/Developer"));
const TechStack          = lazy(() => import("./subpages/TechStack"));

// ===== Feature Flags (toggle features on/off) ================================
export const FEATURE_FLAGS = {
  SHOW_DEVELOPER_SIGNALS: true,   // Toggle Adoption Signals tab and menu
  SHOW_JOBS_TRENDS: false,        // Toggle "Trends" option in Job Postings
};

const showTechStack = getDevFlag('TECH_STACK');
// =============================================================================

// ===== Navigation Config (edit here only) ====================================
const NAV_ITEMS = {
  // Unified metrics view (combines compsheet, dashboard, valuation)
  metrics: {
    path: '/kpis/vendor/table',
    tabLabel: 'Operating KPIs',
    menuItemLabel: 'Operating KPIs',
    breadcrumbLabel: 'Operating KPIs',
    enabled: true,
    showLock: false,
    icon: AssessmentIcon,
  },
  developer: {
    path: '/adoption/project/trends',
    tabLabel: 'Adoption Signals',
    menuItemLabel: 'Adoption Signals',
    breadcrumbLabel: 'Adoption Signals',
    enabled: FEATURE_FLAGS.SHOW_DEVELOPER_SIGNALS,
    showLock: false,
    icon: CodeIcon,
    preview: true,
  },
  techstack: {
    path: '/techstack',
    tabLabel: 'Tech Stack',
    menuItemLabel: 'Tech Stack',
    breadcrumbLabel: 'Tech Stack',
    enabled: showTechStack,
    showLock: false,
    icon: LayersIcon,
    preview: true,
  },
};

// Tab and menu order
const TAB_ORDER = ['metrics', ...(FEATURE_FLAGS.SHOW_DEVELOPER_SIGNALS ? ['developer'] : []), ...(showTechStack ? ['techstack'] : [])];
const TAB_IDX = TAB_ORDER.reduce((acc, key, i) => { acc[key] = i; return acc; }, {});
// Mobile breadcrumb/dropdown order
const MENU_ORDER = TAB_ORDER;
// ============================================================================

function IndustryMetrics() {
  const { isAuthenticated } = useSubscriptionStatus();

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:480px)');
  const [open, setOpen] = useWorkspaceFiltersOpen();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const openBread = Boolean(anchorEl);
  const [selectedButton, setSelectedButton] = useState(0);
  const [menuLabel, setMenuLabel] = useState("Market Coverage"); // will be set by path effect
  const [heightUnit, setHeightUnit] = useState('vh');

  const [selectedTickers, setSelectedTickers] = useState([]);

  useEffect(() => {
    setGlobalActiveTickers(selectedTickers);
  }, [selectedTickers]);

  useEffect(() => {
    const supportsSvh = CSS.supports('height', '1svh');
    if (supportsSvh) setHeightUnit('svh');
  }, []);

  useEffect(() => {
    setSelectedDashboardId('industryMetrics');
  }, [location.pathname]);

  // === Helpers that use the config =================================================
  const resolveKeyByPath = (path) => {
    if (path === '/kpis' || path.startsWith('/kpis/')) return 'metrics';
    if (path === '/developer' || path.startsWith('/developer/')) return 'developer';
    if (path === '/techstack') return 'techstack';

    // support both /path and /path/
    return Object.keys(NAV_ITEMS).find(k => {
      const p = NAV_ITEMS[k].path;
      return path === p || path === `${p}/` || path.startsWith(`${p}/`);
    });
  };

  const getTabFromPath = (path) => {
    const key = resolveKeyByPath(path) || 'metrics';
    return {
      index: TAB_IDX[key] ?? 0,
      label: NAV_ITEMS[key]?.breadcrumbLabel ?? 'Dashboard',
    };
  };
  // ================================================================================

  // Set tab based on current URL on load and whenever it changes
  useEffect(() => {
    const { index, label } = getTabFromPath(location.pathname);
    setSelectedButton(index);
    setMenuLabel(label);
  }, [location.pathname]);

  const handleToggleCollapse = () => setOpen(!open);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleButtonClick = (index) => setSelectedButton(index);

  const handleMenuItemClick = (index, label, link) => {
    setSelectedButton(index);
    setMenuLabel(label);  // will be corrected to breadcrumb label by the location effect after navigate
    handleClose();
    navigate(link);
  };

  const currentNavKey = resolveKeyByPath(location.pathname) || TAB_ORDER[selectedButton] || 'metrics';
  const isOperatingMetricsRoute = currentNavKey === 'metrics';
  const operatingViewId = (() => {
    const match = location.pathname.match(/^\/kpis\/([^/]+)\/([^/]+)/);
    return match?.[2] || null;
  })();
  const operatingSectionId = (() => {
    const match = location.pathname.match(/^\/kpis\/([^/]+)\/([^/]+)/);
    return match?.[1] || null;
  })();
  const CurrentMenuIcon = NAV_ITEMS[currentNavKey]?.icon;
  const sectionHeaderIcon = CurrentMenuIcon ? (
    <CurrentMenuIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
  ) : null;

  if (isOperatingMetricsRoute) {
    return (
      <Suspense>
        <UnifiedMetrics
          selectedTickers={selectedTickers}
          setSelectedTickers={setSelectedTickers}
          open={open}
          handleToggleCollapse={handleToggleCollapse}
          workspaceEnabled
          routeSectionId={operatingSectionId}
          routeViewId={operatingViewId}
        />
      </Suspense>
    );
  }

  return (
    <Box
      sx={{
        height: `calc(100${heightUnit} - 68px)`,
        paddingBottom: '5px',
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        flexGrow: 1
      }}
    >
      {!isOperatingMetricsRoute && (
        <Box
          sx={{
            backgroundColor: theme.background.backgroundColor,
            marginBottom: '0px',
            borderBottomLeftRadius: '0px',
            borderBottomRightRadius: '0px',
            boxShadow: open ? '0px' : (theme.palette.mode === 'light'
              ? '0px 1px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 3px 3px 0px rgba(0,0,0,0.12)'
              : '0px 1px 1px -1px rgba(0,0,0,0.4), 0px 1px 1px 0px rgba(0,0,0,0.24), 0px 3px 3px 0px rgba(0,0,0,0.4)'),
          }}
          elevation={0}
        >
        {(isMobile || !open) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: '5%' }}
              onClick={handleClick}
            >
              {CurrentMenuIcon && (
                <CurrentMenuIcon sx={{ color: theme.palette.primary.main, fontSize: 16, mr: 0.5 }} />
              )}
              <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 'normal' }}>
                {menuLabel}
              </Typography>
              <ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={openBread}
              onClose={handleClose}
              PaperProps={{
                elevation: 1,
                sx: {
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.background.transparent,
                  backdropFilter: 'blur(2px)',
                  width: 'auto',
                },
              }}
            >
              {MENU_ORDER.map((key) => {
                const item = NAV_ITEMS[key];
                if (!item || !item.enabled) return null;
                const IconComponent = item.icon;
                return (
                  <MenuItem
                    key={key}
                    onClick={() => handleMenuItemClick(TAB_IDX[key], item.menuItemLabel, item.path)}
                    sx={{ gap: 1 }}
                  >
                    {IconComponent && <IconComponent sx={{ fontSize: 18, opacity: 0.7 }} />}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {item.menuItemLabel}
                      {item.preview && (
                        <Chip
                          label="Preview"
                          size="small"
                          sx={{
                            height: 14,
                            fontSize: '8px',
                            fontWeight: 600,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      )}
                      {item.showLock && !isAuthenticated && (
                        <LockIcon fontSize="smaller" sx={{ ml: 0.5 }} />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>

            {!open && (
              <ControlButton
                context="menuToolbar"
                variant="iconText"
                icon={<ExpandMoreIcon />}
                sx={{
                  marginTop: '2px',
                  marginRight: '2px',
                }}
                onClick={handleToggleCollapse}
                aria-label="toggle-collapse"
              >
                {(isMobile) ? 'Filters' : 'Expand Menu'}
              </ControlButton>
            )}
          </Box>
        )}

        {!isMobile && (
          <Collapse in={open} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pt: '6px', pb: '4px', gap: 1.5 }}>
              <Typography sx={{ color: theme.palette.primary.main, fontSize: '22px', fontWeight: '600' }}>
                {NAV_ITEMS[currentNavKey]?.menuItemLabel || 'Operating KPIs'}
              </Typography>
            </Box>
            <Box sx={{ pt: '0px', px: '1%', maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Tabs
                  scrollButtons={"auto"}
                  variant={"scrollable"}
                  allowScrollButtonsMobile
                  value={selectedButton}
                  onChange={(e, newValue) => setSelectedButton(newValue)}
                  elevation={0}
                  sx={{
                    height: '28px',
                    minHeight: '28px',
                    borderBottom: `0px solid ${theme.palette.divider}`,
                    maxWidth: '100%',
                    backgroundColor: 'transparent',
                    display: 'flex',
                    justifyContent: 'center',
                    '& .MuiTabs-scrollButtons': {
                      color: theme.palette.text.secondary,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                      '&.Mui-disabled': { opacity: 0.3 }
                    },
                    '& .MuiTab-root': {
                      fontSize: '12px',
                      fontWeight: 500,
                      letterSpacing: '0.01em',
                      borderRadius: '4px 4px 0 0',
                      mx: 0.15,
                      transition: 'all 0.15s ease-out',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      },
                    },
                    '& .Mui-selected': {
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '& .MuiTabs-indicator': {
                      height: 2,
                      borderRadius: '2px 2px 0 0',
                    },
                  }}
                >
                  {TAB_ORDER.map((key) => {
                    const item = NAV_ITEMS[key];
                    if (!item || !item.enabled) return null;
                    const index = TAB_IDX[key];
                    return (
                      <Tab
                        key={key}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {item.tabLabel}
                            {item.preview && (
                              <Chip
                                label="Preview"
                                size="small"
                                sx={{
                                  height: 14,
                                  fontSize: '8px',
                                  fontWeight: 600,
                                  ml: 0.5,
                                  backgroundColor: theme.palette.warning.main,
                                  color: theme.palette.warning.contrastText,
                                  '& .MuiChip-label': { px: 0.5 },
                                }}
                              />
                            )}
                            {item.showLock && !isAuthenticated && (
                              <LockIcon fontSize="smaller" sx={{ ml: 0.5 }} />
                            )}
                          </Box>
                        }
                        style={{ textTransform: 'none' }}
                        sx={{ fontSize: '12px', p: '4px 10px', height: '28px', minHeight: '28px' }}
                        onClick={() => {
                          setSelectedButton(index);
                          navigate(item.path);
                        }}
                      />
                    );
                  })}
                </Tabs>
              </Box>
            </Box>
          </Collapse>
        )}
        </Box>
      )}

      <SectionHeaderProvider value={{ titleIcon: sectionHeaderIcon }}>
        <Suspense>
          {selectedButton === TAB_IDX.metrics && (
            <UnifiedMetrics
              selectedTickers={selectedTickers}
              setSelectedTickers={setSelectedTickers}
              open={open}
              handleToggleCollapse={handleToggleCollapse}
              workspaceEnabled
              routeSectionId={operatingSectionId}
              routeViewId={operatingViewId}
            />
          )}

          {selectedButton === TAB_IDX.developer && (
            <MetricsDeveloper
              selectedTickers={selectedTickers}
              setSelectedTickers={setSelectedTickers}
              open={open}
              handleToggleCollapse={handleToggleCollapse}
            />
          )}

          {selectedButton === TAB_IDX.techstack && (
            <TechStack />
          )}
        </Suspense>
      </SectionHeaderProvider>
    </Box>
  );
}

export default IndustryMetrics;
